/**
 * §P.9 fase 2 · Rentabilidad por grupo.
 *
 * ## «Ese horario de las 16:00 con cuatro niños está costando dinero»
 *
 * Es el título que le puso el contrato, y es el informe entero: no dice cuánto gana
 * el club, dice **qué grupo no se sostiene**. Por eso la tabla sale ordenada de menos
 * a más euros por hora y el peor se marca: lo que se hace con esto es cerrar o juntar
 * un horario, y para eso hay que verlo primero.
 *
 * ## La cuota se reparte entre los grupos a los que va el alumno
 *
 * «Quien paga 30 € y va a dos aporta 15 € a cada uno. Contarla entera en los dos haría
 * que la suma de los grupos duplicara los ingresos reales.» Hay que decirlo en
 * pantalla: un maestro que sume las filas y no cuadre con sus ingresos pensará que el
 * informe está mal, y es justo al contrario.
 */
import { api } from '@/shared/api/client';
import { ErrorDeApi, aProblema } from '@/shared/api/errores';
import { euros } from '@/shared/ui/formato';
import { EnvolturaDeInforme } from '@/club/informes/Envoltura';
import {
  eurosPorHora,
  type FilaDeRentabilidad,
  type Informe,
} from '@/club/informes/tipos';

export function Rentabilidad({ club }: { club: string }) {
  return (
    <EnvolturaDeInforme<FilaDeRentabilidad>
      club={club}
      clave="rentabilidad"
      ruta="group-profitability"
      titulo="Rentabilidad por grupo"
      explicacion={
        <>
          Cuánto entra por cada hora de tatami. Sirve para una cosa concreta:{' '}
          <strong>
            ver el horario con cuatro niños que está costando dinero
          </strong>
          . La cuota de cada alumno se reparte entre los grupos a los que va,
          así que la suma de las filas sí cuadra con los ingresos del mes.
        </>
      }
      vacio={{
        titulo: 'Todavía no hay grupos con cuota',
        explicacion:
          'Este informe cruza los matriculados de cada grupo con lo que pagan y con las horas de su horario. Hace falta al menos un grupo con alumnos y cuota.',
      }}
      pedir={async () => {
        const { data, error } = await api.v1
          .clubs({ club })
          .reports['group-profitability'].get({ query: {} });
        if (error) throw new ErrorDeApi(aProblema(error));
        return data as unknown as Informe<FilaDeRentabilidad>;
      }}
    >
      {(datos) => {
        /*
         * Ya viene ordenado de menos a más, pero **el peor solo cuenta si tiene
         * horario**: un grupo sin horario sale primero con 0 € la hora, y no es que
         * no aporte nada — es que no se sabe.
         */
        const peor = datos.rows.find((f) => eurosPorHora(f) !== null);
        const sinHorario = datos.rows.filter(
          (f) => eurosPorHora(f) === null
        ).length;

        return (
          <>
            {/* Lo que hace inútil la tabla, dicho antes de la tabla. */}
            {sinHorario > 0 && (
              <p className="tenue">
                {sinHorario === 1
                  ? 'Un grupo no tiene horario puesto'
                  : `${sinHorario} grupos no tienen horario puesto`}
                , así que de {sinHorario === 1 ? 'él' : 'ellos'} no se puede
                sacar euros por hora. El horario se pone en el grupo.
              </p>
            )}

            {peor && datos.rows.length > 1 && (
              <div className="aviso" role="status">
                <strong>
                  El grupo que menos aporta por hora es «{peor.group_name}»
                </strong>{' '}
                con {euros(eurosPorHora(peor)!)} la hora y{' '}
                {peor.students === 1 ? 'un alumno' : `${peor.students} alumnos`}
                . No significa que haya que cerrarlo —un grupo de iniciación
                empieza así— pero sí que conviene saber por qué está ahí.
              </div>
            )}

            <div className="tabla-envoltura">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Grupo</th>
                    <th>Sede</th>
                    <th className="num">Alumnos</th>
                    <th className="num">Horas/semana</th>
                    <th className="num">Al mes</th>
                    <th className="num">Por hora</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.rows.map((f) => (
                    <tr key={f.group_id}>
                      <td>{f.group_name}</td>
                      <td>{f.venue_name}</td>
                      <td className="num">
                        {/*
                          «18 de 20» y no solo «18»: sin el aforo al lado, un grupo
                          poco rentable y uno lleno se leen igual.
                        */}
                        {f.capacity === null
                          ? f.students
                          : `${f.students} de ${f.capacity}`}
                      </td>
                      <td className="num">
                        {f.weekly_hours > 0 ? f.weekly_hours : '—'}
                      </td>
                      <td className="num">{euros(f.monthly_cents)}</td>
                      <td className="num">
                        {/*
                          **Sin horario no hay euros por hora.** «0,00 €» diría que
                          el grupo no aporta nada cuando lo que falta es el horario.
                        */}
                        {eurosPorHora(f) === null ? (
                          <span className="tenue">Sin horario</span>
                        ) : (
                          euros(eurosPorHora(f)!)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="tenue">
              Total al mes: {euros(datos.totals['monthly_cents'] ?? 0)} ·{' '}
              {datos.totals['weekly_hours'] ?? 0} horas de tatami a la semana.
            </p>
          </>
        );
      }}
    </EnvolturaDeInforme>
  );
}
